import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { isEmpty } from 'lodash';
import {
  dispatch,
  OnGuestUserAuthenticated,
  updateCustomerId,
  updateTeamMemberId,
  updateScreenOrientation,
  ScreenOrientationType,
  updateAppliedDateRange,
  clearSelectedItemIds,
  useAppSelector,
  getToken,
  useLazyGetWorkspaceQuery,
  updateWorkspace,
  useGetConfigDetailsMutation,
  ConfigModule,
  updateCatalogConfig,
  getIsAuthenticated,
  useLazyGetUserPermissionsQuery,
  updateUserPermissions,
  authenticate,
  clearCollectPaymentSuccess,
  clearSelectedItemsMap,
  updateWorkspaces,
  getWorkspace,
  logOut,
  updateFieldAutomationConfig,
  onAuthSuccess,
  updateDrawerConfig,
  useLazyGetWhatsappConfigQuery,
  updateWaConfig,
  getAppType,
  APP_TYPE,
  updateAdvanceAmountConfig,
} from '@zonofi/zono-money-store';
import {
  i18nService,
  SupportedCurrency,
  SupportedLanguage,
} from '@zonofi/i18n';
import { getDefaultPathFromDrawerConfig } from '../helper/get-drawer-functions';
import { DrawerItems } from '../../types';
import {
  CatalogueView,
  FIELD_OPS_ROUTES,
  SELLER_WEBAPP_ROUTES,
} from '../../constants';
import { defaultDateRangeValue } from '../../helper';

interface UseAppInitializationProps {
  drawerItem: DrawerItems[];
}

export const useAppInitialization = ({
  drawerItem,
}: UseAppInitializationProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const appType = useAppSelector(getAppType);
  const isAuthenticated = useAppSelector(getIsAuthenticated);
  const token = useAppSelector(getToken);
  const workspaceDetails = useAppSelector(getWorkspace);
  const workspaceId = workspaceDetails?.id ?? '';

  const [fetchWorkspaces, fetchWorkspacesRes] = useLazyGetWorkspaceQuery();
  const [getConfigAPI] = useGetConfigDetailsMutation();
  const [getUserPermissionsReq] = useLazyGetUserPermissionsQuery();
  const [getWaConfig, getWaConfigResponse] = useLazyGetWhatsappConfigQuery();

  const originalUrlPath = useRef<string | null>(null);
  const handledUrlTokenRef = useRef<string | null>(null);

  const [shouldClearParamsAfterAuth, setShouldClearParamsAfterAuth] =
    useState(false);
  const [defaultUrlPath, setDefaultUrlPath] = useState<string>('');
  const [isDrawerConfigReady, setIsDrawerConfigReady] = useState(false);

  useEffect(() => {
    if (appType === APP_TYPE.FIELDS_OPS) {
      setDefaultUrlPath(FIELD_OPS_ROUTES.CUSTOMER_LIST);
    } else if (appType === APP_TYPE.SELLER_HUB) {
      setDefaultUrlPath(SELLER_WEBAPP_ROUTES.CONVERSATION_LIST);
    }
  }, [appType]);

  // Extract query parameters
  const queryParams = {
    token: searchParams.get('token'),
    sellerWorkspaceId: searchParams.get('sellerWorkspaceId'),
    teamMemberId: searchParams.get('teamMemberId'),
    customerId: searchParams.get('customerId'),
    userData: searchParams.get('userData'),
  };

  // Handle URL parameters and authentication
  useEffect(() => {
    if (!queryParams.token && !queryParams.userData) return;

    const userData = queryParams.userData
      ? JSON.parse(atob(queryParams.userData))
      : {};

    const authToken = queryParams.token ?? userData?.token;
    if (!authToken) return;

    if (handledUrlTokenRef.current === authToken) return;
    handledUrlTokenRef.current = authToken;
    originalUrlPath.current = location.pathname;

    dispatch(logOut());
    dispatch(OnGuestUserAuthenticated(authToken));

    if (!isEmpty(userData)) {
      dispatch(onAuthSuccess({ ...userData, token: authToken }));
    }

    dispatch(authenticate());
    setShouldClearParamsAfterAuth(true);
  }, [queryParams.token, queryParams.userData, location.pathname]);

  useEffect(() => {
    if (queryParams.teamMemberId) {
      dispatch(updateTeamMemberId(queryParams.teamMemberId));
    }

    if (queryParams.customerId) {
      dispatch(updateCustomerId(queryParams.customerId));
    }
  }, [queryParams.teamMemberId, queryParams.customerId]);

  useEffect(() => {
    if (!token) {
      handledUrlTokenRef.current = null;
      setShouldClearParamsAfterAuth(false);
    }
  }, [token]);

  const handleUserAuthentication = useCallback(async () => {
    try {
      const workspaces = await fetchWorkspaces().unwrap();
      if (!workspaces || isEmpty(workspaces)) {
        dispatch(logOut());
        return;
      }

      const currentWorkspace =
        workspaces.find((w) => w.id === queryParams.sellerWorkspaceId) ??
        workspaces[0];

      dispatch(updateWorkspace(currentWorkspace));
      dispatch(updateWorkspaces(workspaces));
      dispatch(authenticate());

      // Fetch catalog config
      try {
        const configResponse = await getConfigAPI({
          workspaceId: currentWorkspace.id,
          module: ConfigModule.CATALOGUE,
          rollback: false,
        }).unwrap();

        dispatch(
          updateCatalogConfig({
            catalogueView:
              configResponse?.config.catalogueView ?? CatalogueView.ListView,
            displayOutOfStock: configResponse?.config.displayOutOfStock,
          })
        );
      } catch (error) {
        console.error('Failed to fetch catalog config:', error);
      }

      // Fetch user permissions
      try {
        const permissionsResponse = await getUserPermissionsReq().unwrap();
        if (!isEmpty(permissionsResponse)) {
          dispatch(updateUserPermissions(permissionsResponse || []));
        }
      } catch (error) {
        console.error('Failed to fetch user permissions:', error);
      }
    } catch (error) {
      console.error('Failed to fetch workspaces:', error);
    }
  }, [queryParams.sellerWorkspaceId, fetchWorkspaces, getUserPermissionsReq]);

  // Handle workspace authentication
  useEffect(() => {
    if (token && isAuthenticated && !workspaceId) {
      handleUserAuthentication();
    }
  }, [token, isAuthenticated, workspaceId]);

  const fetchDrawerConfig = useCallback(
    async (targetWorkspaceId: string) => {
      if (!appType) return;

      setIsDrawerConfigReady(false);
      let module: ConfigModule;
      if (appType === APP_TYPE.FIELDS_OPS) {
        module = ConfigModule.FIELD_AUTOMATION;
      } else if (appType === APP_TYPE.SELLER_HUB) {
        module = ConfigModule.SELLER_HUB;
      } else {
        return;
      }

      try {
        const configResponse = await getConfigAPI({
          workspaceId: targetWorkspaceId,
          module: module,
          rollback: false,
        }).unwrap();

        const drawerConfig = (configResponse?.config ?? {}) as Record<
          string,
          unknown
        >;

        if (appType === APP_TYPE.FIELDS_OPS) {
          const transformedConfig = Object.fromEntries(
            Object.entries(drawerConfig).map(([key, value]) => [
              key,
              value === 'true' ? true : value === 'false' ? false : value,
            ])
          );
          dispatch(updateFieldAutomationConfig(transformedConfig));
        }

        dispatch(updateDrawerConfig(drawerConfig));
        setDefaultUrlPath(
          getDefaultPathFromDrawerConfig(drawerItem, drawerConfig, appType)
        );
      } catch (error) {
        console.error('Failed to fetch field automation config:', error);
        dispatch(updateFieldAutomationConfig({}));
        dispatch(updateDrawerConfig({}));
        setDefaultUrlPath(
          getDefaultPathFromDrawerConfig(drawerItem, {}, appType)
        );
      } finally {
        setIsDrawerConfigReady(true);
      }
    },
    [appType]
  );

  const fetchOrderConfig = useCallback(
    async (workspaceId: string) => {
      try {
        const configResponse = await getConfigAPI({
          workspaceId: workspaceId,
          module: ConfigModule.ORDER,
          rollback: false,
        }).unwrap();

        dispatch(
          updateAdvanceAmountConfig({
            advancePayment: configResponse?.config?.advancePayment,
          })
        );
      } catch (error) {
        console.error('Failed to fetch catalog config:', error);
      }
    },
    [appType]
  );

  useEffect(() => {
    if (!token || !isAuthenticated || !workspaceId) {
      setIsDrawerConfigReady(false);
      return;
    }

    fetchDrawerConfig(workspaceId);
    // fetchOrderConfig(workspaceId);
  }, [token, isAuthenticated, workspaceId, appType]);

  // Clear parameters after authentication
  useEffect(() => {
    if (
      shouldClearParamsAfterAuth &&
      isAuthenticated &&
      !isEmpty(workspaceId)
    ) {
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('token');
      newParams.delete('sellerWorkspaceId');

      if (originalUrlPath.current) {
        const targetPath = `${originalUrlPath.current}?${newParams.toString()}`;
        navigate(targetPath, { replace: true });
      }
      setShouldClearParamsAfterAuth(false);
    }
  }, [
    shouldClearParamsAfterAuth,
    isAuthenticated,
    workspaceId,
    navigate,
    searchParams,
  ]);

  useEffect(() => {
    if (workspaceDetails) {
      i18nService.updateConfig({
        currency: workspaceDetails.defaultCurrencyCode as SupportedCurrency,
        language: workspaceDetails.isoLanguageCode as SupportedLanguage,
        timezone: workspaceDetails.timeZone,
      });
    }
  }, [workspaceDetails]);

  useEffect(() => {
    if (isAuthenticated && workspaceId) {
      getWaConfig({ sellerWorkspaceId: workspaceId });
    }
  }, [workspaceId, isAuthenticated]);

  useEffect(() => {
    if (getWaConfigResponse.isSuccess && getWaConfigResponse.data) {
      dispatch(updateWaConfig(getWaConfigResponse.data));
    }
  }, [getWaConfigResponse]);

  // Handle orientation changes
  useEffect(() => {
    const handleOrientationChange = () => {
      const rawOrientation =
        window.screen.orientation?.type || ScreenOrientationType.PORTRAIT;
      const normalizedOrientation = rawOrientation.includes('landscape')
        ? ScreenOrientationType.LANDSCAPE
        : ScreenOrientationType.PORTRAIT;

      dispatch(updateScreenOrientation(normalizedOrientation));
    };

    window.addEventListener('orientationchange', handleOrientationChange);
    window.addEventListener('resize', handleOrientationChange);
    handleOrientationChange();

    return () => {
      window.removeEventListener('orientationchange', handleOrientationChange);
      window.removeEventListener('resize', handleOrientationChange);
    };
  }, []);

  // Handle page unload cleanup
  useEffect(() => {
    const handleBeforeUnload = () => {
      dispatch(updateAppliedDateRange(defaultDateRangeValue));
      dispatch(clearSelectedItemIds());
      dispatch(clearCollectPaymentSuccess());
      dispatch(clearSelectedItemsMap());
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  return {
    isLoading:
      fetchWorkspacesRes.isLoading || (!isAuthenticated && !workspaceId),
    originalUrlPath: originalUrlPath.current,
    defaultUrlPath,
    isDrawerConfigReady,
  };
};
